import { useMemo } from 'react'
import { createColumnHelper } from '@atbay/cyber-components'
import Show from '@/ui/components/helpers/Show'
import Typography from '@/ui/components/typography'
import { StyledCustomTable } from '@/ui/components/CustomTable/CustomTable.styles'
import TableEmpty from '@/ui/components/table/table-empty'
import { useAssetsTable } from '@routes/digital-footprint/routes/assets/components/assetsTable/useAssetsTable/useAssetsTable'
import {
    Cell,
    SeverityCell,
    IssuesCell,
    IssueCountItem,
} from '@routes/digital-footprint/routes/assets/components/assetsTable/assetsTable.styles'
import {
    getHighestSeverity,
    countIssuesBySeverity,
    SEVERITY_TEXT_MAPPING,
} from '@routes/digital-footprint/routes/assets/components/assetsTable/utils'
import SeverityIcon from '@/ui/components/svg/SeverityIcon'

interface DevicesTableProps {
    assets: TAsset[]
    emptyDevicesMessage: string
}

const columnHelper = createColumnHelper<TAsset>()

const DevicesTable = ({ assets, emptyDevicesMessage }: DevicesTableProps) => {
    const { sorting, setSorting, handleRowClick, activeAssetId } =
        useAssetsTable({ assets, origin: 'devices_table' })

    const columns = useMemo(
        () => [
            {
                id: 'highestSeverity',
                ...columnHelper.accessor('issues', {
                    cell: (info) => {
                        const asset = info.row.original
                        const highestSeverity = getHighestSeverity(asset.issues)

                        return (
                            <SeverityCell
                                onClick={() =>
                                    handleRowClick(info.row, info.table)
                                }
                                data-testid={`device-table-severity-${highestSeverity}`}
                            >
                                <SeverityIcon severity={highestSeverity} />
                                <Typography
                                    className="table-cell-text"
                                    variant="s-regular"
                                    testId="severity"
                                >
                                    {SEVERITY_TEXT_MAPPING[highestSeverity]}
                                </Typography>
                            </SeverityCell>
                        )
                    },
                    header: () => (
                        <Typography variant="s-regular">Severity</Typography>
                    ),
                    enableSorting: true,
                }),
                width: '20%',
            },
            {
                ...columnHelper.accessor('hostname', {
                    cell: (info) => {
                        const asset = info.row.original

                        return (
                            <Cell
                                onClick={() =>
                                    handleRowClick(info.row, info.table)
                                }
                                data-testid={`device-table-hostname`}
                            >
                                <Typography
                                    className="table-cell-text"
                                    variant="s-regular"
                                    testId="device-hostname"
                                >
                                    {asset.hostname}
                                </Typography>
                            </Cell>
                        )
                    },
                    header: () => (
                        <Typography variant="s-regular">Hostname</Typography>
                    ),
                    enableSorting: true,
                }),
                width: '50%',
            },
            {
                id: 'issuesCount',
                ...columnHelper.accessor('issues', {
                    cell: (info) => {
                        const asset = info.row.original
                        const issuesCount = countIssuesBySeverity(asset.issues)

                        const severityLevels: Array<
                            Exclude<TIssueSeverity, 'clean'>
                        > = ['critical', 'important', 'moderate']

                        return (
                            <IssuesCell
                                onClick={() =>
                                    handleRowClick(info.row, info.table)
                                }
                                data-testid={`device-table-issues`}
                            >
                                {severityLevels.map((severity) => (
                                    <Show
                                        key={severity}
                                        when={issuesCount?.[severity]}
                                    >
                                        <IssueCountItem
                                            className={`count ${severity}`}
                                            data-testid="device-issues-severity-count"
                                        >
                                            <SeverityIcon
                                                severity={severity}
                                                dataTestId={`severity-${severity}`}
                                            />
                                            <Typography
                                                className="table-cell-text"
                                                variant="s-regular"
                                            >
                                                {issuesCount?.[severity]}
                                            </Typography>
                                        </IssueCountItem>
                                    </Show>
                                ))}
                            </IssuesCell>
                        )
                    },
                    header: () => (
                        <Typography variant="s-regular">Issues</Typography>
                    ),
                    enableSorting: true,
                }),
                width: '15%',
            },
        ],
        [handleRowClick]
    )

    return (
        <StyledCustomTable
            activeRow={activeAssetId}
            getRowId={(row: TAsset) => row.id}
            tableHeight={700}
            columns={columns}
            data={assets}
            sorting={sorting}
            setSorting={setSorting}
            emptyState={<TableEmpty message={emptyDevicesMessage} />}
        />
    )
}

export default DevicesTable
